import { useEffect, useMemo, useState } from "react";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import TextField from "../../../components/Form/TextField";
import AutocompleteField from "../../../components/Form/AutocompleteField";
import Loader from "../../../components/Loaders/Loader";
import { useForm } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp } from "../../../lib/redux";
import { startUpdateAdvertiserKpiCpaManual } from "../../../actions/owner";
import { startGetAdvertiserKpiSummary } from "../../../actions/user";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import { PLATFORMS_SUPPORTED } from "../../../utils/constants";
import { buildKpiSummaryFromManual } from "../utils/kpiSummaryMerge";
import {
  ADMIN_KPI_CPA_CURRENCY_CODES,
  ADMIN_PRIMARY_KPI_OPTIONS,
} from "../../../utils/options";
import {
  BUTTON_CANCEL,
  BUTTON_SAVE,
  FIELD_REQUIRED,
  MODAL_MODIFY_KPI_CPA_CURRENCY_LABEL,
  MODAL_MODIFY_KPI_CPA_PRIMARY_LABEL,
  MODAL_MODIFY_KPI_CPA_SAVE_SUCCESS,
  MODAL_MODIFY_KPI_CPA_TARGET_LABEL,
  MODAL_MODIFY_KPI_CPA_TITLE,
  MODAL_MODIFY_KPI_CPA_VOLUMEN_LABEL,
} from "../../../i18n/keysTranslations";

const buildDefaultPlatformTargets = () => {
  const platformTargets = {};
  PLATFORMS_SUPPORTED.forEach((platform) => {
    platformTargets[platform] = { targetCpa: "", currency: "USD" };
  });
  return platformTargets;
};

const applyPlatformRowFromKpiSummaryParts = ({ platformTargets, summary }) => {
  const parts = summary?.targetCpaParts;
  if (Array.isArray(parts)) {
    parts.forEach((part) => {
      if (!part?.platform) {
        return;
      }
      const n = Number(part.targetCpa);
      platformTargets[part.platform] = {
        targetCpa: Number.isFinite(n) && n > 0 ? String(part.targetCpa) : "",
        currency:
          typeof part.currency === "string" && part.currency.trim()
            ? part.currency.trim().toUpperCase()
            : "USD",
      };
    });
  }
};

const buildDefaultValues = ({ initialKpiSummary }) => {
  const platformTargets = buildDefaultPlatformTargets();

  if (initialKpiSummary?.targetCpaParts?.length) {
    applyPlatformRowFromKpiSummaryParts({
      platformTargets,
      summary: initialKpiSummary,
    });
  } else if (initialKpiSummary?.platformTargets) {
    PLATFORMS_SUPPORTED.forEach((platform) => {
      const row = initialKpiSummary.platformTargets[platform];
      if (!row) {
        return;
      }
      const n = Number(row.targetCpa);
      platformTargets[platform] = {
        targetCpa: Number.isFinite(n) && n > 0 ? String(row.targetCpa) : "",
        currency:
          typeof row.currency === "string" && row.currency.trim()
            ? row.currency.trim().toUpperCase()
            : "USD",
      };
    });
  }

  const pk = initialKpiSummary?.primaryKpi?.trim();
  const volumenN = Number(initialKpiSummary?.volume);
  return {
    primaryKpi: pk || null,
    volume: Number.isFinite(volumenN) && volumenN > 0 ? String(volumenN) : "",
    platformTargets,
  };
};

const buildPayloadFromValues = (values) => {
  const platformTargets = {};
  PLATFORMS_SUPPORTED.forEach((platform) => {
    const raw = values.platformTargets?.[platform]?.targetCpa;
    const n = parseFloat(String(raw ?? "").replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      return;
    }
    const cur = values.platformTargets?.[platform]?.currency || "USD";
    platformTargets[platform] = {
      targetCpa: n,
      currency: String(cur).trim().toUpperCase() || "USD",
    };
  });

  const pk = (values.primaryKpi ?? "").toString().trim();
  const volumenRaw = values.volume;
  const volumenN = parseFloat(String(volumenRaw ?? "").replace(",", "."));
  const volume =
    Number.isFinite(volumenN) && volumenN > 0 ? Math.round(volumenN) : null;

  if (!pk && Object.keys(platformTargets).length === 0 && volume == null) {
    return null;
  }

  return {
    primaryKpi: pk || null,
    platformTargets,
    ...(volume != null ? { volume } : {}),
  };
};

const ModalModifyKpiCpa = ({
  modalOpen,
  onCloseModal,
  advertiserID,
  businessName = "",
  initialKpiSummary,
  onCallbackSave = () => {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [kpiSummary, setKpiSummary] = useState(null);
  const [isLoadingKpiSummary, setIsLoadingKpiSummary] = useState(false);
  const shouldFetchKpiSummary = initialKpiSummary === undefined;

  useEffect(() => {
    if (!modalOpen || !advertiserID) {
      return;
    }

    let cancelled = false;

    const loadKpiSummary = async () => {
      if (!shouldFetchKpiSummary) {
        setKpiSummary(initialKpiSummary ?? null);
        return;
      }

      setIsLoadingKpiSummary(true);
      try {
        const response = await dispatch(
          startGetAdvertiserKpiSummary({ advertiserID })
        );
        if (!cancelled) {
          setKpiSummary(response?.ok ? (response.kpiSummary ?? null) : null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingKpiSummary(false);
        }
      }
    };

    loadKpiSummary();

    return () => {
      cancelled = true;
    };
  }, [
    modalOpen,
    advertiserID,
    initialKpiSummary,
    shouldFetchKpiSummary,
    dispatch,
  ]);

  const defaultValues = useMemo(
    () => buildDefaultValues({ initialKpiSummary: kpiSummary }),
    [kpiSummary]
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (modalOpen && !isLoadingKpiSummary) {
      reset(buildDefaultValues({ initialKpiSummary: kpiSummary }));
    }
  }, [modalOpen, kpiSummary, isLoadingKpiSummary, reset]);

  const primaryKpiOptions = useMemo(
    () => [
      ...ADMIN_PRIMARY_KPI_OPTIONS.map(({ value }) => ({
        value,
        label: value,
      })),
    ],
    []
  );

  const currencyOptions = useMemo(
    () =>
      ADMIN_KPI_CPA_CURRENCY_CODES.map((code) => ({
        value: code,
        label: code,
      })),
    []
  );

  const kpiFilterOptions = useMemo(
    () =>
      createFilterOptions({
        matchFrom: "any",
        stringify: (option) =>
          `${option?.value ?? ""} ${t(option?.label ?? "")}`,
      }),
    [t]
  );

  const currencyFilterOptions = useMemo(
    () =>
      createFilterOptions({
        matchFrom: "any",
        stringify: (option) => `${option?.value ?? ""} ${option?.label ?? ""}`,
      }),
    []
  );

  const primaryKpiRules = useMemo(
    () => ({
      validate: (value) => {
        if (value != null && String(value).trim() !== "") {
          return true;
        }
        return t(FIELD_REQUIRED);
      },
    }),
    [t]
  );

  const onSubmit = async (values) => {
    if (!advertiserID) {
      return;
    }
    const draft = buildPayloadFromValues(values);
    const kpiSummaryToSave = draft ? buildKpiSummaryFromManual(draft) : null;
    setIsSubmitting(true);
    try {
      const response = await dispatch(
        startUpdateAdvertiserKpiCpaManual({
          advertiserID,
          kpiSummary: kpiSummaryToSave,
        })
      );

      if (response?.ok) {
        setKpiSummary(kpiSummaryToSave);
        onCallbackSave({ advertiserID, kpiSummary: kpiSummaryToSave });
        await SimpleAlert({
          title: t(MODAL_MODIFY_KPI_CPA_SAVE_SUCCESS),
          icon: "success",
        });
        onCloseModal();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const emptyRules = { required: false };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(MODAL_MODIFY_KPI_CPA_TITLE, {
        name: businessName || "—",
      })}
      maxWidth="sm"
      fullWidth
      showCloseButton={true}
    >
      {isLoadingKpiSummary ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <Loader size={32} hasMarginTop={false} />
        </Box>
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "100%",
          }}
        >
          <AutocompleteField
            fullWidth
            label={t(MODAL_MODIFY_KPI_CPA_PRIMARY_LABEL)}
            name="primaryKpi"
            variant="filled"
            color="primary"
            control={control}
            rules={primaryKpiRules}
            errors={errors}
            options={primaryKpiOptions}
            doTranslate
            filterOptions={kpiFilterOptions}
          />

          <TextField
            fullWidth
            label={t(MODAL_MODIFY_KPI_CPA_VOLUMEN_LABEL)}
            name="volume"
            variant="filled"
            color="primary"
            InputLabelProps={{ shrink: true }}
            control={control}
            rules={emptyRules}
            errors={errors}
            type="number"
            inputProps={{ min: 1, step: 1 }}
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
            {t(MODAL_MODIFY_KPI_CPA_TARGET_LABEL)} /{" "}
            {t(MODAL_MODIFY_KPI_CPA_CURRENCY_LABEL)}
          </Typography>

          {PLATFORMS_SUPPORTED.map((platform) => (
            <Box
              key={platform}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 1.5,
                alignItems: { sm: "flex-start" },
              }}
            >
              <Box sx={{ minWidth: { sm: 120 }, pt: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {t(platform)}
                </Typography>
              </Box>
              <TextField
                fullWidth
                label={t(MODAL_MODIFY_KPI_CPA_TARGET_LABEL)}
                name={`platformTargets.${platform}.targetCpa`}
                variant="filled"
                color="primary"
                InputLabelProps={{ shrink: true }}
                control={control}
                rules={emptyRules}
                errors={errors}
                type="number"
              />
              <Box sx={{ width: { xs: "100%", sm: 180 }, flexShrink: 0 }}>
                <AutocompleteField
                  fullWidth
                  label={t(MODAL_MODIFY_KPI_CPA_CURRENCY_LABEL)}
                  name={`platformTargets.${platform}.currency`}
                  variant="filled"
                  color="primary"
                  control={control}
                  rules={emptyRules}
                  errors={errors}
                  options={currencyOptions}
                  doTranslate
                  filterOptions={currencyFilterOptions}
                />
              </Box>
            </Box>
          ))}

          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_CANCEL,
              onClick: onCloseModal,
            }}
            propsPrimaryButton={{
              showButton: true,
              text: BUTTON_SAVE,
              loading: isSubmitting,
              type: "submit",
              onClick: () => {},
            }}
          />
        </Box>
      )}
    </ModalBasicLayout>
  );
};

export default ModalModifyKpiCpa;
